import React, { useCallback, useEffect } from 'react';
import { Link } from '@react-router';
import { pxToRem } from '@withjoy/joykit/theme';
import { ButtonV2, Flex, TextV2 } from '@withjoy/joykit';
import { ResponsiveDialog } from '@shared/components/ResponsiveDialog';
import { getLocalStorage, useTranslation } from '@shared/core';
import { usePlanningRoutes } from '@apps/admin/routes/Planning/hooks/usePlanningRoutes';

import { getLocalStorageFlag } from '../DashboardContentV2.utils';
import { useDashboardTelemetry } from '../../../Dashboard.telemetry';
import { LOCAL_STORAGE_HAS_PRESENTED_WON_BOOKING_DIALOG } from '../DashboardContentV2.constants';
import { closeButtonOverrides, dialogOverrides, drawerOverrides } from './WonBookingDialog.styles';

const DEFAULT_IMAGE_URL = 'https://withjoy.com/assets/public/hotelbooking/won-booking-hotel.png';
const localStorage = getLocalStorage();

interface WonBookingDialogProps
  extends Readonly<{
    isOpen: boolean;
    onClose: () => void;
    eventId: string;
  }> {}

export const WonBookingDialog: React.FC<WonBookingDialogProps> = props => {
  const { isOpen, onClose, eventId } = props;
  const { t2 } = useTranslation('bookingDialog');
  const { title, subTitle, ctaText } = t2('wonBookingDialog');

  const { hotelBlockPath } = usePlanningRoutes();
  const { wonBookingDialogDismissClicked, wonBookingDialogCTAClicked, wonBookingDialog } = useDashboardTelemetry();

  const handleDialogDismiss = useCallback(() => {
    wonBookingDialogDismissClicked();
    onClose();
  }, [wonBookingDialogDismissClicked, onClose]);

  useEffect(() => {
    if (isOpen) {
      wonBookingDialog.enter();
    }
  }, [isOpen, wonBookingDialog]);

  useEffect(() => {
    if (isOpen) {
      localStorage.setItem(getLocalStorageFlag(LOCAL_STORAGE_HAS_PRESENTED_WON_BOOKING_DIALOG, eventId), 'true');
    }
  }, [eventId, isOpen]);

  return (
    <ResponsiveDialog
      dialogOverrides={dialogOverrides}
      drawerOverrides={drawerOverrides}
      closeButtonOverrides={closeButtonOverrides}
      disableCloseOnEscapeKeyClick={true}
      disableCloseOnOutsideClick={true}
      isOpen={isOpen}
      onClose={handleDialogDismiss}
      hideDivider={true}
      removeBodyPadding={true}
    >
      <Flex flexDirection={{ _: 'column', sm: 'row' }}>
        <Flex
          position="relative"
          width={{ _: '100%', sm: '34%' }}
          height={{ _: pxToRem(188), sm: 'unset' }}
          borderTopLeftRadius={{ sm: pxToRem(12) }}
          borderBottomLeftRadius={{ sm: pxToRem(12) }}
          backgroundSize="cover"
          backgroundRepeat="no-repeat"
          backgroundPosition="center"
          backgroundImage={`url('${DEFAULT_IMAGE_URL}')`}
        />
        <Flex
          width={{ _: '100%', sm: '66%' }}
          flexDirection="column"
          backgroundColor="white"
          paddingX={{ _: 7, sm: 8 }}
          paddingY={{ _: 7, sm: pxToRem(56) }}
          borderTopRightRadius={{ sm: pxToRem(12) }}
          borderBottomRightRadius={{ sm: pxToRem(12) }}
        >
          <TextV2 typographyVariant={{ _: 'hed5', sm: 'display1' }} paddingBottom={5}>
            {title}
          </TextV2>
          <TextV2 paddingBottom={{ _: 6, sm: 8 }} fontSize={{ _: pxToRem(16), sm: pxToRem(18) }} lineHeight={{ _: pxToRem(24), sm: pxToRem(27) }} fontWeight={400} color="mono12">
            {subTitle}
          </TextV2>
          <ButtonV2 width={['100%']} intent="neutral" variant="solid" shape="rounded" as={Link} to={hotelBlockPath} onClick={() => wonBookingDialogCTAClicked()}>
            {ctaText}
          </ButtonV2>
        </Flex>
      </Flex>
    </ResponsiveDialog>
  );
};
